import { useEffect, useState, useRef, useCallback } from "react";
import api, { API, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { MessageSquare, Send, Paperclip, ChevronLeft, Search } from "lucide-react";
import { toast } from "sonner";
import { logger } from "../lib/logger";

const ROLE_LABEL = { admin: "Admin", teacher: "Enseignant", parent: "Parent" };
const ROLE_COLOR = {
  admin: "bg-[#FBE8DC] text-[#C86444]",
  teacher: "bg-[#E0E7FF] text-[#1E3A8A]",
  parent: "bg-[#E0F2E9] text-[#1E7A4D]",
};

function initials(name) {
  return (name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

// Hook custom encapsulant le polling thread + contacts
function useMessaging(activeId, context) {
  const [contacts, setContacts] = useState([]);
  const [thread, setThread] = useState({ messages: [], contact: null });

  const loadContacts = useCallback(() => {
    api.get("/messages/contacts", { params: context ? { context } : {} })
      .then((r) => setContacts(r.data))
      .catch((e) => logger.debug("loadContacts:", e));
  }, [context]);

  useEffect(() => {
    loadContacts();
    const t = setInterval(loadContacts, 15000);
    return () => clearInterval(t);
  }, [loadContacts]);

  useEffect(() => {
    if (!activeId) return undefined;
    const load = () => api.get(`/messages/thread/${activeId}`).then((r) => setThread(r.data)).catch((e) => logger.debug("loadThread:", e));
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [activeId]);

  const reloadThread = useCallback(async () => {
    if (!activeId) return;
    const r = await api.get(`/messages/thread/${activeId}`);
    setThread(r.data);
    loadContacts();
  }, [activeId, loadContacts]);

  return { contacts, thread, reloadThread };
}

export default function MessagingPanel({ currentUser, context }) {
  const [active, setActive] = useState(null);
  const { contacts, thread, reloadThread } = useMessaging(active, context);
  const [filter, setFilter] = useState("");

  return (
    <div className="bg-white border border-[#EAE6DF] rounded-2xl overflow-hidden grid md:grid-cols-[300px_1fr] h-[600px]" data-testid="messaging-panel">
      <ContactsSidebar
        contacts={contacts}
        active={active}
        onSelect={setActive}
        filter={filter}
        onFilter={setFilter}
        currentUserId={currentUser?.id}
      />
      <main className={`flex flex-col min-h-0 h-full overflow-hidden ${active ? "flex" : "hidden md:flex"}`}>
        {!active && <EmptyThread />}
        {active && thread.contact && (
          <>
            <ThreadHeader contact={thread.contact} onBack={() => setActive(null)} />
            <ThreadMessages messages={thread.messages} currentUserId={currentUser?.id} />
            <MessageComposer activeId={active} onSent={reloadThread} />
          </>
        )}
      </main>
    </div>
  );
}

function EmptyThread() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center px-6 text-[#8a92a3]">
      <MessageSquare className="w-12 h-12 mb-3 text-[#EAE6DF]" strokeWidth={1.5} />
      <p className="text-sm">Sélectionnez un interlocuteur pour démarrer la conversation.</p>
    </div>
  );
}

function ContactsSidebar({ contacts, active, onSelect, filter, onFilter, currentUserId }) {
  const filtered = contacts.filter((c) => !filter || c.name.toLowerCase().includes(filter.toLowerCase()));
  return (
    <aside className={`border-r border-[#EAE6DF] flex flex-col min-h-0 h-full overflow-hidden ${active ? "hidden md:flex" : "flex"}`}>
      <div className="p-3 border-b border-[#EAE6DF]">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8a92a3]" />
          <input
            placeholder="Rechercher..."
            value={filter}
            onChange={(e) => onFilter(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-[#FCFBF8] border border-[#EAE6DF] rounded-full focus:outline-none focus:border-[#1E3A8A]"
            data-testid="msg-search"
          />
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto msg-scrollbar" data-testid="contacts-scroll-area">
        {filtered.length === 0 && (
          <div className="p-6 text-center text-xs text-[#8a92a3]">
            {contacts.length === 0 ? "Aucun interlocuteur disponible." : "Aucun résultat."}
          </div>
        )}
        {filtered.map((c) => (
          <ContactRow key={c.id} contact={c} active={active === c.id} currentUserId={currentUserId} onSelect={onSelect} />
        ))}
      </div>
    </aside>
  );
}

function ContactRow({ contact: c, active, currentUserId, onSelect }) {
  const lastSnippet = c.last_message
    ? (c.last_message.sender_id === currentUserId ? "Vous : " : "") + c.last_message.body
    : ROLE_LABEL[c.role];
  return (
    <button
      onClick={() => onSelect(c.id)}
      className={`w-full text-left px-3 py-3 border-b border-[#EAE6DF] hover:bg-[#FCFBF8] flex gap-3 items-start transition-colors ${active ? "bg-[#FCFBF8]" : ""}`}
      data-testid={`contact-${c.id}`}
    >
      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${ROLE_COLOR[c.role]}`}>
        {initials(c.name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium text-sm text-[#1A2547] truncate">{c.name}</span>
          {c.last_message && <span className="text-[10px] text-[#8a92a3] shrink-0">{formatTime(c.last_message.created_at)}</span>}
        </div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <span className="text-xs text-[#5c6374] truncate">{lastSnippet}</span>
          {c.unread_count > 0 && (
            <Badge className="bg-[#C86444] text-white border-0 rounded-full px-1.5 py-0 text-[10px] h-4 shrink-0">{c.unread_count}</Badge>
          )}
        </div>
      </div>
    </button>
  );
}

function ThreadHeader({ contact, onBack }) {
  return (
    <header className="px-4 py-3 border-b border-[#EAE6DF] flex items-center gap-3">
      <Button variant="ghost" size="sm" className="md:hidden h-8 w-8 p-0" onClick={onBack} data-testid="msg-back">
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold ${ROLE_COLOR[contact.role]}`}>
        {initials(contact.name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-[Outfit] font-semibold text-[#1A2547] truncate">{contact.name}</div>
        <Badge className={`border-0 rounded-full text-[10px] ${ROLE_COLOR[contact.role]}`}>{ROLE_LABEL[contact.role]}</Badge>
      </div>
    </header>
  );
}

function ThreadMessages({ messages, currentUserId }) {
  const bottomRef = useRef(null);
  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);
  return (
    <div className="flex-1 min-h-0 overflow-y-auto msg-scrollbar p-4 space-y-3 bg-[#FCFBF8]" data-testid="thread-scroll-area">
      {messages.length === 0 && (
        <div className="text-center text-xs text-[#8a92a3] py-12">Aucun message pour l'instant. Écrivez le premier !</div>
      )}
      {messages.map((m) => (
        <MessageBubble key={m.id} message={m} mine={m.sender_id === currentUserId} />
      ))}
      <div ref={bottomRef} />
    </div>
  );
}

function MessageBubble({ message: m, mine }) {
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
      <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${mine ? "bg-[#1E3A8A] text-white" : "bg-white border border-[#EAE6DF] text-[#1A2547]"}`}>
        <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
        {m.attachment_path && (
          <a
            href={`${API}/files/${m.attachment_path}`}
            target="_blank"
            rel="noreferrer"
            className={`mt-1.5 inline-flex items-center gap-1 text-xs underline ${mine ? "text-white/90" : "text-[#1E3A8A]"}`}
          >
            <Paperclip className="w-3 h-3" /> {m.attachment_name || "Pièce jointe"}
          </a>
        )}
        <div className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-[#8a92a3]"}`}>{formatTime(m.created_at)}</div>
      </div>
    </div>
  );
}

function useAttachmentUpload() {
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await fetch(`${API}/upload`, { method: "POST", body: fd, credentials: "include" });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "Échec upload");
      }
      const data = await r.json();
      setAttachment({ path: data.path, name: f.name });
      toast.success("Pièce jointe prête");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const reset = () => setAttachment(null);

  return { attachment, uploading, fileRef, handleFile, reset };
}

function MessageComposer({ activeId, onSent }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const { attachment, uploading, fileRef, handleFile, reset: resetAttachment } = useAttachmentUpload();

  const send = async (e) => {
    e?.preventDefault?.();
    if (!body.trim() && !attachment) return;
    setSending(true);
    try {
      await api.post("/messages", {
        recipient_id: activeId,
        body: body.trim() || (attachment ? "(pièce jointe)" : ""),
        attachment_path: attachment?.path || null,
        attachment_name: attachment?.name || null,
      });
      setBody("");
      resetAttachment();
      await onSent();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={send} className="border-t border-[#EAE6DF] p-3 flex items-end gap-2 bg-white">
      <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={handleFile} className="hidden" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="h-10 w-10 p-0 shrink-0"
        data-testid="msg-attach"
      >
        <Paperclip className="w-4 h-4" />
      </Button>
      <div className="flex-1">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send(e);
            }
          }}
          placeholder="Votre message... (Entrée pour envoyer)"
          rows={1}
          className="resize-none rounded-2xl min-h-[40px]"
          data-testid="msg-input"
        />
        {attachment && (
          <div className="mt-1.5 text-xs text-[#1E7A4D] flex items-center gap-1">
            <Paperclip className="w-3 h-3" /> {attachment.name}
            <button type="button" onClick={resetAttachment} className="text-[#B0413E] ml-2 underline">
              retirer
            </button>
          </div>
        )}
      </div>
      <Button
        type="submit"
        disabled={sending || (!body.trim() && !attachment)}
        className="rounded-full bg-[#1E3A8A] hover:bg-[#14265E] h-10 w-10 p-0 shrink-0"
        data-testid="msg-send"
      >
        <Send className="w-4 h-4" />
      </Button>
    </form>
  );
}
